import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { FieldError } from '@/components/ui/fieldError';
import { Textarea } from '@/components/ui/textarea';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { PLATFORM_SCOPE_CODE, SELECTABLE_SCOPE_CODES } from '@/constants/scopes';
import { useCreateRoleMutation } from '@/hooks/useCreateRoleMutation';
import { usePermissionsQuery } from '@/hooks/usePermissionsQuery';
import { useReplaceRolePermissionsMutation } from '@/hooks/useReplaceRolePermissionsMutation';
import { useRoleDetailQuery } from '@/hooks/useRoleDetailQuery';
import { isBlank } from '@/utils/formValidation';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { PermissionSelector } from './PermissionSelector';

interface RoleFormModalProps {
  roleId: string | null;
  onClose: () => void;
}

export function RoleFormModal({ roleId, onClose }: RoleFormModalProps) {
  const { t } = useTranslation(['admin', 'common']);
  const isCreateMode = roleId === null;

  const permissionsQuery = usePermissionsQuery();
  const roleDetailQuery = useRoleDetailQuery(roleId);
  const createRoleMutation = useCreateRoleMutation();
  const replacePermissionsMutation = useReplaceRolePermissionsMutation();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scopeCode, setScopeCode] = useState<string>(PLATFORM_SCOPE_CODE);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>([]);
  const [showErrors, setShowErrors] = useState(false);

  const nameError = showErrors === true && isBlank(name) ? 'validation:requiredField' : null;

  const loadedRole = roleDetailQuery.data;

  useEffect(
    function prefillFromLoadedRole() {
      if (loadedRole === undefined) {
        return;
      }
      setName(loadedRole.name);
      setDescription(loadedRole.description === null ? '' : loadedRole.description);
      setScopeCode(loadedRole.scopeCode);
      setSelectedPermissionIds(loadedRole.permissionIds);
    },
    [loadedRole]
  );

  const isSystemRole = loadedRole !== undefined && loadedRole.isSystem === true;
  const arePermissionsReadOnly = isCreateMode === false && isSystemRole === true;
  const areDetailsReadOnly = isCreateMode === false;
  const isSaving = createRoleMutation.isPending || replacePermissionsMutation.isPending;

  function togglePermission(permissionId: string) {
    setSelectedPermissionIds(function updateSelection(previousSelection) {
      if (previousSelection.includes(permissionId)) {
        return previousSelection.filter(function removeId(existingId) {
          return existingId !== permissionId;
        });
      }
      return [...previousSelection, permissionId];
    });
  }

  function handleMutationError(mutationError: unknown) {
    const backendMessage = resolveApiErrorMessage(mutationError);
    showErrorNotification(backendMessage.length > 0 ? backendMessage : t('admin:errors.generic'));
  }

  function submitRole(formEvent: FormEvent<HTMLFormElement>) {
    formEvent.preventDefault();
    if (isCreateMode === true) {
      if (isBlank(name)) {
        setShowErrors(true);
        return;
      }
      createRoleMutation.mutate(
        {
          name: name.trim(),
          description: description.trim(),
          scopeCode,
          permissionIds: selectedPermissionIds,
        },
        {
          onSuccess: function handleCreated() {
            showSuccessNotification(t('admin:roles.createSuccess'));
            onClose();
          },
          onError: handleMutationError,
        }
      );
      return;
    }
    if (roleId === null) {
      return;
    }
    replacePermissionsMutation.mutate(
      { roleId, permissionIds: selectedPermissionIds },
      {
        onSuccess: function handleUpdated() {
          showSuccessNotification(t('admin:roles.updateSuccess'));
          onClose();
        },
        onError: handleMutationError,
      }
    );
  }

  const isInitialLoading =
    permissionsQuery.isLoading === true ||
    (isCreateMode === false && roleDetailQuery.isLoading === true);

  return (
    <ModalOverlay onRequestClose={onClose}>
      <form
        onSubmit={submitRole}
        className="w-full max-w-2xl max-h-[90vh] flex flex-col bg-zoowhite border border-zooborder rounded-3xl shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-zooborder">
          <h2 className="font-bold text-zooindigo">
            {isCreateMode === true ? t('admin:roles.createTitle') : t('admin:roles.editTitle')}
          </h2>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('common:actions.close')}
            onClick={onClose}
          >
            <Icon name="X" className="text-zoomuted" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {isInitialLoading === true ? (
            <div className="py-12 flex justify-center">
              <LoadingSpinner className="w-7 h-7" />
            </div>
          ) : (
            <>
              {isSystemRole === true ? (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-zoovpalelavender text-xs font-medium text-zooindigo">
                  <Icon name="Lock" className="text-zooviolet" />
                  {t('admin:roles.systemReadOnly')}
                </div>
              ) : null}

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="role-name">{t('admin:roles.nameLabel')}</Label>
                  <Input
                    id="role-name"
                    readOnly={areDetailsReadOnly}
                    value={name}
                    hasError={nameError !== null}
                    placeholder={t('admin:roles.namePlaceholder')}
                    onChange={function updateName(changeEvent) {
                      setName(changeEvent.target.value);
                    }}
                  />
                  <FieldError messageKey={nameError} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="role-scope">{t('admin:roles.scopeLabel')}</Label>
                  <SearchableSelect
                    inputId="role-scope"
                    isDisabled={areDetailsReadOnly}
                    value={scopeCode}
                    options={SELECTABLE_SCOPE_CODES.map(function toOption(scopeOption) {
                      return { value: scopeOption, label: t('scope.' + scopeOption) };
                    })}
                    onChange={function updateScope(nextValue) {
                      setScopeCode(nextValue);
                    }}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="role-description">{t('admin:roles.descriptionLabel')}</Label>
                <Textarea
                  id="role-description"
                  rows={2}
                  readOnly={areDetailsReadOnly}
                  value={description}
                  placeholder={t('admin:roles.descriptionPlaceholder')}
                  onChange={function updateDescription(changeEvent) {
                    setDescription(changeEvent.target.value);
                  }}
                />
              </div>

              <div>
                <div className="text-sm font-bold text-zooindigo mb-3">
                  {t('admin:roles.permissionsLabel')}
                </div>
                <PermissionSelector
                  permissions={permissionsQuery.data === undefined ? [] : permissionsQuery.data}
                  selectedPermissionIds={selectedPermissionIds}
                  isReadOnly={arePermissionsReadOnly}
                  onTogglePermission={togglePermission}
                />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-zooborder bg-zoooffwhite">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          {arePermissionsReadOnly === true ? null : (
            <Button type="submit" isLoading={isSaving}>
              {isCreateMode === true
                ? t('admin:roles.createAction')
                : t('common:actions.saveChanges')}
            </Button>
          )}
        </div>
      </form>
    </ModalOverlay>
  );
}
